import { getRouteShell, normalizeRouteShellPath } from "./routeShell.js";
import { markThirdPartyAnalyticsPresent } from "./thirdPartyAnalyticsIsolation.js";
import { hasKeyUrlParams, hasPrivateUrlParams } from "./privateUrlParams.js";

// Offlico's pages are on www.offlico.com and LocalPickr's on localpickr.com and
// find.localpickr.com since 25 Oct 2026 (business.offlico.com and
// find.offlico.com only redirect now but stay listed for old cached pages).
export const ALLOWED_HOSTS = new Set([
  "www.offlico.com", "offlico.com", "business.offlico.com", "find.offlico.com",
  "localpickr.com", "www.localpickr.com", "find.localpickr.com",
]);
const NOISE_QUERY_PARAMS = new Set([
  "gtm_latency",
  "fbclid",
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
]);
const ATTRIBUTION_STORAGE_KEY = "offlico.attribution.v1";
const GOOGLE_ADS_CONVERSION_ID = (import.meta.env?.VITE_GOOGLE_ADS_CONVERSION_ID || "AW-18063953664").trim();
const GOOGLE_ADS_CONVERSION_LABELS = {
  sign_up: (import.meta.env?.VITE_GOOGLE_ADS_SIGN_UP_LABEL || "UbDICNH-2JUcEICeyKVD").trim(),
  customer_request_submitted: (
    import.meta.env?.VITE_GOOGLE_ADS_CUSTOMER_REQUEST_SUBMITTED_LABEL || "fri0CJmJtcgcEICeyKVD"
  ).trim(),
  professional_contact_click: (
    import.meta.env?.VITE_GOOGLE_ADS_PROFESSIONAL_CONTACT_CLICK_LABEL || "1QaJCMqatcgcEICeyKVD"
  ).trim(),
  booking_intent: (import.meta.env?.VITE_GOOGLE_ADS_BOOKING_INTENT_LABEL || "XkPuCOfNncgcEICeyKVD").trim(),
};
const ATTRIBUTION_QUERY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "campaign",
  "profession",
  "plan",
  "fbclid",
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
];
const META_PIXEL_ID = (import.meta.env?.VITE_META_PIXEL_ID || "1000223732484358").trim();
const PUBLIC_AUTH_ANALYTICS_ROUTES = new Set(["/signin", "/signup"]);

export function isThirdPartyAnalyticsRoute(pathname = "/") {
  const normalizedPathname = normalizeRouteShellPath(pathname);
  return (
    getRouteShell(normalizedPathname) === "marketing" ||
    PUBLIC_AUTH_ANALYTICS_ROUTES.has(normalizedPathname)
  );
}

function currentPathname() {
  return typeof window !== "undefined" ? window.location.pathname : "/";
}

function canTrackGoogle(pathname = currentPathname()) {
  return (
    typeof window !== "undefined" &&
    typeof window.gtag === "function" &&
    ALLOWED_HOSTS.has(window.location.hostname) &&
    isThirdPartyAnalyticsRoute(pathname)
  );
}

// The page title Google Analytics may see: index.html leaves out a title set
// while the address had private parameters. Passed with every Google event.
// (Google Ads reads the browser's title itself: no page may put what a
// customer typed in its title; the results page names the district only.)
function googlePageTitle() {
  const safeTitle = typeof window !== "undefined" ? window.__offlico_google_page_title : undefined;
  if (typeof safeTitle === "string") return safeTitle;
  if (typeof window !== "undefined" && hasPrivateUrlParams(window.location.href)) return "LocalPickr";
  return typeof document !== "undefined" ? document.title : "";
}

function canTrackMetaHost(pathname = currentPathname()) {
  return (
    typeof window !== "undefined" &&
    Boolean(META_PIXEL_ID) &&
    ALLOWED_HOSTS.has(window.location.hostname) &&
    isThirdPartyAnalyticsRoute(pathname)
  );
}

// Meta's pixel sends the page's address (dl) and the one before it (rl) with
// every request, and can't be given cleaned ones. So it isn't loaded and gets
// no events while either has a sign-in or payment key (privateUrlParams.js):
// the hub (?token=), the request form with a key, password reset and Google
// sign-in returns, gift cards (?t=), Stripe returns; nor for the rest of a
// page where index.html locked it (a key came into the address after it was
// set up). Search results (?where=, ?lat=&lng=) get it as before; Google gets
// cleaned addresses (index.html).
export function isMetaPixelBlockedHere() {
  if (typeof window === "undefined") return true;
  return window.__offlico_meta_locked === true || hasKeyUrlParams(window.location.href) || hasKeyUrlParams(document.referrer);
}

function ensureMetaPixel(pathname = currentPathname()) {
  if (!canTrackMetaHost(pathname)) return false;
  if (isMetaPixelBlockedHere()) return false;
  markThirdPartyAnalyticsPresent();

  if (!window.fbq) {
    // Standard Meta Pixel bootstrap
    // eslint-disable-next-line func-names
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        // eslint-disable-next-line prefer-rest-params
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      t.setAttribute("data-offlico-third-party-analytics", "meta");
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  }

  if (!window.__offlico_meta_pixel_initialized && typeof window.fbq === "function") {
    window.fbq("init", META_PIXEL_ID);
    window.__offlico_meta_pixel_initialized = true;
  }

  return typeof window.fbq === "function";
}

function readAttributionStore() {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeAttributionStore(value) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage failures.
  }
}

function cleanAttribution(value = {}) {
  const next = {};
  for (const [key, raw] of Object.entries(value)) {
    const trimmed = String(raw || "").trim();
    if (!trimmed) continue;
    next[key] = trimmed;
  }
  return next;
}

function extractAttribution(searchParams) {
  const next = {};
  for (const key of ATTRIBUTION_QUERY_KEYS) {
    const value = searchParams.get(key);
    if (!value) continue;
    next[key] = value;
  }
  return cleanAttribution(next);
}

export function isAdminPathname(pathname = "/") {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

function mergeAttribution(nextValues = {}) {
  const existing = readAttributionStore();
  const merged = cleanAttribution({ ...existing, ...nextValues });
  if (Object.keys(merged).length > 0) {
    writeAttributionStore(merged);
  }
  return merged;
}

function inferTrafficSource(attribution = {}) {
  const utmSource = String(attribution.utm_source || "").toLowerCase();
  if (utmSource && /(facebook|instagram|meta)/.test(utmSource)) return "facebook";
  if (utmSource && /(google|youtube)/.test(utmSource)) return "google";
  if (attribution.fbclid) return "facebook";
  if (attribution.gclid || attribution.gbraid || attribution.wbraid) return "google";
  return undefined;
}

function buildAttributionEventParams(attribution = {}) {
  const params = {};

  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "campaign",
    "plan",
  ]) {
    if (attribution[key]) {
      params[key] = attribution[key];
    }
  }

  if (attribution.profession) {
    params.profession_type = attribution.profession;
  }

  if (attribution.fbclid) {
    params.has_fbclid = 1;
  }

  if (attribution.gclid || attribution.gbraid || attribution.wbraid) {
    params.has_gclid = 1;
  }

  if (attribution.msclkid) {
    params.has_msclkid = 1;
  }

  const sourceHint = inferTrafficSource(attribution);
  if (sourceHint) {
    params.traffic_source_hint = sourceHint;
  }

  return params;
}

export function normalizeTrackingPathname(pathname = "/") {
  const normalizedPathname = normalizeRouteShellPath(pathname);

  if (normalizedPathname === "/book") {
    return normalizedPathname;
  }

  const bookingMatch = normalizedPathname.match(
    /^\/book\/[^/?#]+(?:\/(checkout|policy|manage\/[^/?#]+))?$/i,
  );

  if (bookingMatch) {
    const suffix = bookingMatch[1] || "";
    if (suffix.startsWith("manage/")) return "/book/:slug/manage";
    if (suffix === "checkout") return "/book/:slug/checkout";
    if (suffix === "policy") return "/book/:slug/policy";
    return "/book/:slug";
  }

  if (/^\/shared-calendar\/invite\/[^/?#]+$/i.test(normalizedPathname)) {
    return "/shared-calendar/invite/:token";
  }

  if (/^\/shared-calendar\/[^/?#]+$/i.test(normalizedPathname)) {
    return "/shared-calendar/:slug";
  }

  return normalizedPathname;
}

export function buildSanitizedLocation(rawPath) {
  const base = typeof window !== "undefined" ? window.location.origin : "https://www.offlico.com";
  const currentPath = rawPath || `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const url = new URL(currentPath || "/", base);

  const extractedAttribution = extractAttribution(url.searchParams);
  let attribution = mergeAttribution(extractedAttribution);

  for (const key of [...url.searchParams.keys()]) {
    if (NOISE_QUERY_PARAMS.has(key) || key.toLowerCase().startsWith("utm_")) {
      url.searchParams.delete(key);
    }
  }

  const pagePath = normalizeTrackingPathname(url.pathname || "/");
  const routeShell = getRouteShell(url.pathname || "/");
  if (routeShell === "marketing") {
    attribution = mergeAttribution({ signup_source_path: pagePath });
  }

  return {
    pagePath,
    pageLocation: `${url.origin}${pagePath}`,
    routeShell,
    attributionParams: buildAttributionEventParams(attribution),
  };
}

function withStoredAttribution(params = {}) {
  return {
    ...buildAttributionEventParams(readAttributionStore()),
    ...params,
  };
}

export function getTrackingAttribution() {
  return cleanAttribution(readAttributionStore());
}

export function getSignupSourcePath() {
  const value = String(readAttributionStore().signup_source_path || "").trim();
  if (!value.startsWith("/") || value.startsWith("//")) return undefined;
  return value;
}

export function trackMetaEvent(name, params = {}) {
  if (!ensureMetaPixel()) return;
  window.fbq("track", name, withStoredAttribution(params));
}

export function trackMetaCustomEvent(name, params = {}) {
  if (!ensureMetaPixel()) return;
  window.fbq("trackCustom", name, withStoredAttribution(params));
}

export function trackPageView({ path } = {}) {
  const { pagePath, pageLocation, routeShell, attributionParams } = buildSanitizedLocation(path);
  if (!isThirdPartyAnalyticsRoute(pagePath)) return;

  if (canTrackGoogle(pagePath)) {
    window.gtag("event", "page_view", {
      page_title: googlePageTitle(),
      page_location: pageLocation,
      page_path: pagePath,
      route_shell: routeShell,
      ...attributionParams,
    });
  }

  trackMetaEvent("PageView", {
    route_shell: routeShell,
    ...attributionParams,
  });
}

export function trackEvent(name, params = {}) {
  if (!canTrackGoogle()) return;
  window.gtag("event", name, { page_title: googlePageTitle(), ...withStoredAttribution(params) });
}

export function trackGoogleAdsConversion(action, params = {}) {
  if (!canTrackGoogle()) return;
  const label = GOOGLE_ADS_CONVERSION_LABELS[action];
  if (!GOOGLE_ADS_CONVERSION_ID || !label) return;
  const payload = withStoredAttribution(params);
  const { value = 1.0, currency = "GBP", ...rest } = payload;
  window.gtag("event", "conversion", {
    send_to: `${GOOGLE_ADS_CONVERSION_ID}/${label}`,
    value,
    currency,
    page_title: googlePageTitle(),
    ...rest,
  });
}

export function resetTrackingAttribution() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}
